import ObjectId from 'bson-objectid'
import {
  Button,
  Card,
  Checkbox,
  Col,
  Collapse,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  message,
  theme,
  Tag
} from 'antd'
import React, { useEffect, useState } from 'react'
import CustomRow from '../../../reusable-components/CustomRow'
import BillingTool from './BillingTool'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faFileLines, faRefresh, faSearch, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'

import './styles/Billing.css'
import { isReception } from '../../../lib/profile-utils'
import CoreEnums from '../../../../core/utils/enums'
import { handleError } from '../../../lib/utils'
import {
  createBillingRecord,
  updateBillingRecord,
  getPlanSubOptions,
  goodXBenefitCheck,
  goodXResponse,
  getProcedurePrice,
  getProductPrice
} from '../../utils/utils'
import { findAgiliteUser, createAgiliteUser, updateAgiliteUser } from '../../../Auth/utils/utils'
import { useSelector } from 'react-redux'

const Billing = ({
  billing,
  billingItems,
  setBillingItems,
  bookingData,
  setBookingData,
  handleGetBillingRecords,
  billingRecordsLoading,
  setBillingSyncRequired,
  billingWebSocket
}) => {
  const authState = useSelector((state) => state.auth)
  const state = useSelector((state) => state)
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedMacros, setSelectedMacros] = useState([])
  const [readOnly, setReadOnly] = useState(false)
  const [filterQuery, setFilterQuery] = useState('')
  const [benefitCheckLoading, setBenefitCheckLoading] = useState(false)
  const [benefitCheckResponse, setBenefitCheckResponse] = useState(null)
  const [servicesMacros, setServicesMacros] = useState([])
  // Add invoice type state - default to 'medical' if patient has medical aid, otherwise 'private'
  console.log('Benefits Check Response', benefitCheckResponse)

  /**
   * Parse date from benefits response format to proper date string
   */
  const parseBenefitsDate = (dateString) => {
    if (!dateString) return ''
    try {
      const date = new Date(dateString)
      return date.toISOString().split('T')[0] // Return YYYY-MM-DD format
    } catch (error) {
      console.error('Error parsing date:', dateString, error)
      return ''
    }
  }

  /**
   * Extract and normalize patient data from benefits check response
   */
  const extractPatientData = (patientObj, medicalAidInfo) => {
    if (!patientObj) return null

    return {
      idNo: patientObj?.CommonPersonInfo?.[0]?.IdentityNumber?.[0] || '',
      firstName: patientObj?.FirstName?.[0] || '',
      lastName: patientObj?.CommonPersonInfo?.[0]?.Surname?.[0] || '',
      dateOfBirth: parseBenefitsDate(patientObj?.DateOfBirth?.[0]),
      gender: patientObj?.Gender?.[0] === 'M' ? 'Male' : patientObj?.Gender?.[0] === 'F' ? 'Female' : '',
      medicalAid: {
        name: medicalAidInfo?.name || '',
        number: medicalAidInfo?.policyNumber || '',
        plan: medicalAidInfo?.plan || '',
        dependantNumber: patientObj?.DependantCode?.[0] || ''
      },
      extraData: {
        isActive: true,
        role: {
          type: 'patient',
          isManager: false
        },
        relation: patientObj?.DependantType?.[0] === 'Principal party' ? 'main_member' : 'dependant'
      },
      // Additional fields from benefits response
      _benefitsData: {
        dependantCode: patientObj?.DependantCode?.[0] || '',
        dependantType: patientObj?.DependantType?.[0] || '',
        medAidStatus: patientObj?.MedAidStatusDesc?.[0] || '',
        medAidToDate: patientObj?.MedAidToDate?.[0] || ''
      }
    }
  }

  /**
   * Extract medical aid information from benefits response
   */
  const extractMedicalAidInfo = (benefitResponse) => {
    try {
      const commonInfo = benefitResponse?.ProviderClaimResponse?.CommonProviderClaimResponseInfo?.[0]
      const memberValidation = commonInfo?.MemberValidation?.[0]
      const claimHeader = memberValidation?.CommonClaimHeaderInfo?.[0]

      return {
        name: claimHeader?.Payor?.[0]?.Name?.[0] || '',
        policyNumber: claimHeader?.Policy?.[0]?.PolicyNumber?.[0] || '',
        plan: claimHeader?.Payor?.[0]?.OptionOrPlanReference?.[0] || '',
        schemeCode: '', // Not directly available in this response
        planCode: claimHeader?.Payor?.[0]?.OptionOrPlanReference?.[0] || ''
      }
    } catch (error) {
      console.error('Error extracting medical aid info:', error)
      return {
        name: '',
        policyNumber: '',
        plan: '',
        schemeCode: '',
        planCode: ''
      }
    }
  }

  /**
   * Process benefits check response and update/create patient accounts
   */
  const processBenefitsResponse = async (benefitResponse) => {
    try {
      const patients =
        benefitResponse?.ProviderClaimResponse?.MemberValidationResponse?.[0]?.ResponseDetail?.[0]?.Patient

      if (!patients || !Array.isArray(patients)) {
        message.error('No patient data found in benefits response')
        return
      }

      const medicalAidInfo = extractMedicalAidInfo(benefitResponse)
      console.log('Extracted Medical Aid Info:', medicalAidInfo)

      // Find main member (Principal party)
      const mainMember = patients.find((p) => p?.DependantType?.[0] === 'Principal party')
      const dependants = patients.filter((p) => p?.DependantType?.[0] !== 'Principal party')

      console.log('Main Member:', mainMember)
      console.log('Dependants:', dependants)

      if (!mainMember) {
        message.error('No main member found in benefits response')
        return
      }

      const currentPatient = bookingData?.patientRecord
      const mainMemberIdNo = mainMember?.CommonPersonInfo?.[0]?.IdentityNumber?.[0]
      const currentPatientIdNo = currentPatient?.idNo

      console.log('Current Patient ID:', currentPatientIdNo)
      console.log('Main Member ID from benefits:', mainMemberIdNo)

      // Check if current patient is actually the main member
      if (currentPatientIdNo === mainMemberIdNo) {
        console.log('Current patient IS the main member - updating existing account')
        // Current patient is the main member - update them
        await updateMainMemberAccount(mainMember, medicalAidInfo)
      } else {
        console.log('Current patient is NOT the main member - creating new main member account')
        // Current patient is a dependant - create new main member and restructure
        await createMainMemberAndRestructure(mainMember, medicalAidInfo, patients)
      }

      // Process remaining dependants
      if (dependants.length > 0) {
        await processDependants(dependants, medicalAidInfo, mainMember)
      }

      message.success(
        `Successfully updated account structure${
          dependants.length > 0 ? ` with ${dependants.length + 1} family members` : ''
        }`
      )
    } catch (error) {
      console.error('Error processing benefits response:', error)
      message.error('Failed to process benefits check response: ' + error.message)
    }
  }

  /**
   * Create new main member account and restructure the current patient as dependant
   */
  const createMainMemberAndRestructure = async (mainMemberData, medicalAidInfo, allPatients) => {
    try {
      const currentPatient = bookingData?.patientRecord
      const mainMemberIdNo = mainMemberData?.CommonPersonInfo?.[0]?.IdentityNumber?.[0]

      console.log('Debug - Current Patient:', currentPatient)
      console.log('Debug - Current Patient ID:', currentPatient?._id)
      console.log('Debug - Main Member Data:', mainMemberData)
      console.log('Debug - Main Member ID:', mainMemberIdNo)

      // Check for patient ID in multiple possible fields
      const patientId = currentPatient?._id || currentPatient?.id || currentPatient?.recordId

      if (!patientId) {
        console.error('Available patient fields:', Object.keys(currentPatient || {}))
        throw new Error(
          `Missing current patient ID - no valid ID found in patient object. Available fields: ${Object.keys(
            currentPatient || {}
          ).join(', ')}`
        )
      }

      if (!mainMemberIdNo) {
        throw new Error('Missing main member ID from benefits response - check CommonPersonInfo structure')
      }

      // First check if main member already exists
      console.log('Checking if main member already exists...')
      let existingMainMember = null
      try {
        existingMainMember = await findAgiliteUser({ idNo: mainMemberIdNo })
        console.log('Found existing main member:', existingMainMember.firstName, existingMainMember.lastName)
      } catch (error) {
        console.log('Main member does not exist yet, will create new one')
      }

      // If main member already exists, use them instead of creating a duplicate
      if (existingMainMember) {
        console.log('Using existing main member instead of creating duplicate')

        // Update current patient to be a dependant of the existing main member
        const currentPatientInBenefits = allPatients.find(
          (p) => p?.CommonPersonInfo?.[0]?.IdentityNumber?.[0] === currentPatient.idNo
        )

        const dependantUpdateData = {
          mainMemberId: existingMainMember._id,
          extraData: {
            ...currentPatient.extraData,
            relation: 'dependant'
          }
        }

        // Update medical aid info if available from benefits check
        if (currentPatientInBenefits?.DependantCode?.[0]) {
          dependantUpdateData.medicalAid = {
            ...currentPatient.medicalAid,
            ...extractPatientData(mainMemberData, medicalAidInfo).medicalAid,
            dependantNumber: currentPatientInBenefits.DependantCode[0]
          }
        }

        await updateAgiliteUser(patientId, dependantUpdateData)
        console.log('Updated current patient as dependant of existing main member')

        // Update local booking data
        setBookingData((prev) => ({
          ...prev,
          patientRecord: {
            ...prev.patientRecord,
            ...dependantUpdateData
          }
        }))

        return existingMainMember
      }

      console.log('Creating new main member account...')

      // Extract main member data
      const mainMemberExtractedData = extractPatientData(mainMemberData, medicalAidInfo)

      // Create new main member account with current patient's billing context
      const newMainMemberData = {
        ...mainMemberExtractedData,
        // Use current patient's billing ID and entity context
        billingId: currentPatient.billingId,
        homeEntity: currentPatient.homeEntity || authState.agiliteUser?.homeEntity,
        extraData: {
          ...mainMemberExtractedData.extraData,
          entities: currentPatient.extraData?.entities || [authState.agiliteUser?.homeEntity],
          relation: 'main_member'
        }
      }

      // Create the new main member account
      const newMainMember = await createAgiliteUser(newMainMemberData, false, authState.agiliteUser?._id)
      console.log('Created new main member:', newMainMember.firstName, newMainMember.lastName)

      // Update current patient to be a dependant of the new main member
      console.log('Converting current patient to dependant...')

      // Find current patient in benefits response to get their dependant details
      const currentPatientInBenefits = allPatients.find(
        (p) => p?.CommonPersonInfo?.[0]?.IdentityNumber?.[0] === currentPatient.idNo
      )

      const dependantUpdateData = {
        mainMemberId: newMainMember._id, // Use the MongoDB ObjectId, not the ID number
        extraData: {
          ...currentPatient.extraData,
          relation: 'dependant'
        }
      }

      // Update medical aid dependant number if available
      if (currentPatientInBenefits?.DependantCode?.[0]) {
        dependantUpdateData.medicalAid = {
          ...currentPatient.medicalAid,
          ...mainMemberExtractedData.medicalAid, // Use main member's medical aid details
          dependantNumber: currentPatientInBenefits.DependantCode[0] // But keep dependant's number
        }
      }

      await updateAgiliteUser(patientId, dependantUpdateData)
      console.log('Updated current patient as dependant')

      // Update local booking data to reflect the restructure
      setBookingData((prev) => ({
        ...prev,
        patientRecord: {
          ...prev.patientRecord,
          ...dependantUpdateData
        }
      }))

      return newMainMember
    } catch (error) {
      console.error('Error creating main member and restructuring:', error)
      throw error
    }
  }

  /**
   * Update the current patient account with main member data
   */
  const updateMainMemberAccount = async (mainMemberData, medicalAidInfo) => {
    try {
      const extractedData = extractPatientData(mainMemberData, medicalAidInfo)
      const currentPatient = bookingData?.patientRecord

      const patientId = currentPatient?._id || currentPatient?.id || currentPatient?.recordId

      if (!patientId) {
        console.error('Available patient fields:', Object.keys(currentPatient || {}))
        throw new Error(
          `No current patient found to update - no valid ID found. Available fields: ${Object.keys(
            currentPatient || {}
          ).join(', ')}`
        )
      }

      // Prepare update data - only update non-empty values
      const updateData = {}

      // Update basic info if available
      if (extractedData.idNo && extractedData.idNo !== currentPatient.idNo) {
        updateData.idNo = extractedData.idNo
      }
      if (extractedData.firstName && extractedData.firstName !== currentPatient.firstName) {
        updateData.firstName = extractedData.firstName
      }
      if (extractedData.lastName && extractedData.lastName !== currentPatient.lastName) {
        updateData.lastName = extractedData.lastName
      }
      if (extractedData.dateOfBirth && extractedData.dateOfBirth !== currentPatient.dateOfBirth) {
        updateData.dateOfBirth = extractedData.dateOfBirth
      }
      if (extractedData.gender && extractedData.gender !== currentPatient.gender) {
        updateData.gender = extractedData.gender
      }

      // Update medical aid information
      const currentMedicalAid = currentPatient.medicalAid || {}
      const newMedicalAid = { ...currentMedicalAid }
      let medicalAidUpdated = false

      if (extractedData.medicalAid.name && extractedData.medicalAid.name !== currentMedicalAid.name) {
        newMedicalAid.name = extractedData.medicalAid.name
        medicalAidUpdated = true
      }
      if (extractedData.medicalAid.number && extractedData.medicalAid.number !== currentMedicalAid.number) {
        newMedicalAid.number = extractedData.medicalAid.number
        medicalAidUpdated = true
      }
      if (extractedData.medicalAid.plan && extractedData.medicalAid.plan !== currentMedicalAid.plan) {
        newMedicalAid.plan = extractedData.medicalAid.plan
        medicalAidUpdated = true
      }

      if (medicalAidUpdated) {
        updateData.medicalAid = newMedicalAid
      }

      // Only update if there are changes
      if (Object.keys(updateData).length > 0) {
        console.log('Updating main member with data:', updateData)
        await updateAgiliteUser(patientId, updateData)

        // Update local booking data to reflect changes
        setBookingData((prev) => ({
          ...prev,
          patientRecord: {
            ...prev.patientRecord,
            ...updateData
          }
        }))

        console.log('Main member account updated successfully')
      } else {
        console.log('No updates needed for main member account')
      }
    } catch (error) {
      console.error('Error updating main member account:', error)
      throw error
    }
  }

  /**
   * Process and create/update dependant accounts
   */
  const processDependants = async (dependants, medicalAidInfo, mainMemberData) => {
    try {
      const currentPatient = bookingData?.patientRecord
      const mainMemberIdNo = mainMemberData?.CommonPersonInfo?.[0]?.IdentityNumber?.[0]

      if (!currentPatient?.billingId) {
        throw new Error('Current patient billingId not found')
      }

      // Check if main member account exists, if not try to find it
      let mainMemberAccount = null
      try {
        mainMemberAccount = await findAgiliteUser({ idNo: mainMemberIdNo })
      } catch (error) {
        console.log('Main member account not found in system')
      }

      // If we can't find the main member, we can't process dependants properly
      if (!mainMemberAccount) {
        console.error('Cannot process dependants without a main member account')
        message.error('Main member account must be created before processing dependants')
        return []
      }

      const processedDependants = []

      for (const dependant of dependants) {
        const extractedData = extractPatientData(dependant, medicalAidInfo)

        if (!extractedData.idNo) {
          console.warn('Skipping dependant without ID number:', dependant)
          continue
        }

        // Check if dependant already exists
        let existingDependant = null
        try {
          existingDependant = await findAgiliteUser({ idNo: extractedData.idNo })
        } catch (error) {
          console.log('Dependant not found, will create new:', extractedData.idNo)
        }

        if (existingDependant) {
          // Update existing dependant
          const updateData = {}

          // Update basic info if different
          if (extractedData.firstName && extractedData.firstName !== existingDependant.firstName) {
            updateData.firstName = extractedData.firstName
          }
          if (extractedData.lastName && extractedData.lastName !== existingDependant.lastName) {
            updateData.lastName = extractedData.lastName
          }
          if (extractedData.dateOfBirth && extractedData.dateOfBirth !== existingDependant.dateOfBirth) {
            updateData.dateOfBirth = extractedData.dateOfBirth
          }
          if (extractedData.gender && extractedData.gender !== existingDependant.gender) {
            updateData.gender = extractedData.gender
          }

          // Update medical aid info
          if (extractedData.medicalAid.name || extractedData.medicalAid.number) {
            updateData.medicalAid = {
              ...existingDependant.medicalAid,
              ...extractedData.medicalAid
            }
          }

          // Ensure mainMemberId is set to the main member's MongoDB ObjectId
          if (mainMemberAccount?._id && existingDependant.mainMemberId !== mainMemberAccount._id) {
            updateData.mainMemberId = mainMemberAccount._id
          }

          if (Object.keys(updateData).length > 0) {
            await updateAgiliteUser(existingDependant._id, updateData)
            console.log(`Updated existing dependant: ${extractedData.firstName} ${extractedData.lastName}`)
          }

          processedDependants.push({ ...existingDependant, ...updateData })
        } else {
          // Create new dependant
          const newDependantData = {
            ...extractedData,
            mainMemberId: mainMemberAccount?._id, // Use the MongoDB ObjectId of the main member
            // Use the main member's billing ID for billing linkage
            billingId: currentPatient.billingId,
            // Set entity from current user context
            homeEntity: currentPatient.homeEntity || authState.agiliteUser?.homeEntity,
            extraData: {
              ...extractedData.extraData,
              entities: currentPatient.extraData?.entities || [authState.agiliteUser?.homeEntity],
              relation: 'dependant'
            }
          }

          const newDependant = await createAgiliteUser(newDependantData, false, authState.agiliteUser?._id)
          console.log(`Created new dependant: ${extractedData.firstName} ${extractedData.lastName}`)
          processedDependants.push(newDependant)
        }
      }

      // Update main member's dependants array (use main member account if it exists)
      if (processedDependants.length > 0) {
        const dependantIds = processedDependants.map((dep) => dep._id || dep.id)

        // Include current patient if they're also a dependant
        const currentPatientIdNo = currentPatient?.idNo
        const currentPatientDbId = currentPatient?._id || currentPatient?.id || currentPatient?.recordId
        if (currentPatientIdNo !== mainMemberIdNo && currentPatientDbId) {
          dependantIds.push(currentPatientDbId)
        }

        const targetAccountId = mainMemberAccount?._id || currentPatientDbId
        await updateAgiliteUser(targetAccountId, {
          dependants: dependantIds
        })

        console.log(`Updated main member with ${dependantIds.length} dependants`)
      }
    } catch (error) {
      console.error('Error processing dependants:', error)
      throw error
    }
  }
  const [selectedInvoiceType, setSelectedInvoiceType] = useState(
    bookingData.selectedInvoiceType || (bookingData.patientRecord?.medicalAid?.name ? 'medical' : 'private')
  )
  const { token } = theme.useToken()

  useEffect(() => {
    if (isReception()) {
      if (bookingData?.status !== CoreEnums.bookingStatuses.reception) {
        setReadOnly(true)
      }
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    // Get services config from clinic data that have billing macro items (equivalent to billing macros)
    const clinicServices = state.clinics.data
      .find((i) => i._id === state.auth.agiliteUser.extraData.clinics[0])
      ?.servicesConfig?.filter((i) => i.name && i.billingMacroItems && i.billingMacroItems.length > 0)

    setServicesMacros(clinicServices || [])
    // eslint-disable-next-line
  }, [state.clinics.data, state.auth.agiliteUser.extraData.clinics])

  const handleCheckboxChange = (checked, macro) => {
    const tmpSelectedMacros = [...selectedMacros]
    const tmpIndex = tmpSelectedMacros.findIndex((item) => item.name === macro.name)

    if (tmpIndex === -1 && checked) {
      tmpSelectedMacros.push(macro)
    } else if (tmpIndex !== -1 && !checked) {
      tmpSelectedMacros.splice(tmpIndex, 1)
    }

    setSelectedMacros(tmpSelectedMacros)
  }

  // Function to recalculate all billing items when invoice type changes
  const recalculateAllBillingItems = async () => {
    const tmpBillingItems = [...billingItems]

    for (const item of tmpBillingItems) {
      if (item.type === 'Medicine' || item.type === 'Material') {
        try {
          // Check if invoice type is private OR patient has no medical aid
          if (selectedInvoiceType === 'private' || !bookingData.patientRecord.medicalAid.name) {
            item.medicalAid = '0'
            item.patient = item.privatePrice || '0'
            item.total = item.privatePrice || '0'
          } else {
            const priceResponse = await getProductPrice(
              item.type.toLowerCase(),
              item.code,
              bookingData.patientRecord.medicalAid.planOptionCode || '95C'
            )

            if (priceResponse) {
              item.medicalAid = (priceResponse.price - priceResponse.patientLiable).toFixed(2)
              item.patient = priceResponse.patientLiable
              item.total = priceResponse.price
            }
          }
        } catch (e) {
          console.log('Error getting product price:', e)
        }
      }

      if (item.type === 'Procedure' || item.type === 'Consultation') {
        try {
          // Check if invoice type is private OR patient has no medical aid
          if (selectedInvoiceType === 'private' || !bookingData.patientRecord.medicalAid.name) {
            item.medicalAid = '0'
            item.patient = item.privatePrice || '0'
            item.total = item.privatePrice || '0'
          } else {
            let isContract = false
            if (
              bookingData.clinicRecord.billingProfile.schemeContracts.includes(
                bookingData.patientRecord.medicalAid.schemeCode
              )
            ) {
              isContract = true
            }

            const priceResponse = await getProcedurePrice(
              item.code,
              bookingData.patientRecord.medicalAid.planOptionCode || '95C',
              isContract,
              bookingData.clinicRecord.billingProfile
            )

            if (priceResponse) {
              item.medicalAid = priceResponse.price
              item.patient = 0
              item.total = priceResponse.price
            }
          }
        } catch (e) {
          console.log('Error getting procedure price:', e)
        }
      }

      // Update the billing record in the database
      if (item._id) {
        try {
          billingWebSocket.socket?.send(
            JSON.stringify({ bookingRef: bookingData._id, userRef: authState.agiliteUser._id })
          )
          updateBillingRecord(item._id, item)
        } catch (e) {
          console.log('Error updating billing record:', e)
        }
      }
    }

    setBillingItems(tmpBillingItems)
  }

  // Effect to recalculate when invoice type changes
  useEffect(() => {
    if (billingItems.length > 0 && !billingRecordsLoading) {
      recalculateAllBillingItems()
    }
    // eslint-disable-next-line
  }, [selectedInvoiceType])

  const handleAddMacro = async () => {
    const tmpBillingItems = [...billingItems]
    const tmpLastItem = tmpBillingItems[tmpBillingItems.length - 1]
    const entries = []

    try {
      tmpBillingItems.pop()

      selectedMacros.forEach((macro) => {
        // Use billingMacroItems instead of items
        macro.billingMacroItems.forEach((item) => {
          const record = {
            _id: ObjectId().toHexString(),
            bookingRef: bookingData._id,
            code: item.code,
            name: item.name,
            qty: item.qty,
            type: item.type,
            medicalAid: '0',
            patient: '0',
            total: '0',
            privatePrice: item.privatePrice,
            packSize: undefined,
            nappiCode: undefined
          }

          tmpBillingItems.push(record)
          entries.push(record)
        })
      })

      // Apply pricing logic to each billing item
      for (const item of entries) {
        if (item.type === 'Medicine' || item.type === 'Material') {
          try {
            let priceResponse = null

            // Check if invoice type is private OR patient has no medical aid
            if (
              selectedInvoiceType === 'private' ||
              !bookingData.patientRecord.medicalAid.name ||
              (item.privatePrice !== '0' && item.privatePrice && item.privatePrice.trim() !== '')
            ) {
              item.medicalAid = '0'
              item.patient = item.privatePrice || '0'
              item.total = item.privatePrice || '0'
            } else {
              priceResponse = await getProductPrice(
                item.type.toLowerCase(),
                item.code,
                bookingData.patientRecord.medicalAid.planOptionCode || '95C'
              )

              if (priceResponse) {
                item.medicalAid = (priceResponse.price - priceResponse.patientLiable).toFixed(2)
                item.patient = priceResponse.patientLiable
                item.total = priceResponse.price
              }
            }
          } catch (e) {
            console.log('Error getting product price:', e)
          }
        }

        if (item.type === 'Procedure' || item.type === 'Consultation') {
          let isContract = false

          if (
            bookingData.clinicRecord.billingProfile.schemeContracts.includes(
              bookingData.patientRecord.medicalAid.schemeCode
            )
          ) {
            isContract = true
          }

          try {
            let priceResponse = null

            // Check if invoice type is private OR patient has no medical aid
            if (
              selectedInvoiceType === 'private' ||
              !bookingData.patientRecord.medicalAid.name ||
              (item.privatePrice !== '0' && item.privatePrice && item.privatePrice.trim() !== '')
            ) {
              item.medicalAid = '0'
              item.patient = item.privatePrice || '0'
              item.total = item.privatePrice || '0'
            } else {
              priceResponse = await getProcedurePrice(
                item.code,
                bookingData.patientRecord.medicalAid.planOptionCode || '95C',
                isContract,
                bookingData.clinicRecord.billingProfile
              )

              if (priceResponse) {
                item.medicalAid = priceResponse.price
                item.patient = 0
                item.total = priceResponse.price
              }
            }
          } catch (e) {
            console.log('Error getting procedure price:', e)
          }
        }
      }

      if (tmpLastItem) {
        tmpBillingItems.push(tmpLastItem)
      }

      setBillingItems(tmpBillingItems)
      setSelectedMacros([])
      setModalOpen(false)

      // No need to await this function
      createBillingRecord(entries)
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const filteredMacros = () => {
    // Use services macros instead of billing macros
    let tmpMacros = [...servicesMacros]

    if (filterQuery) {
      tmpMacros = tmpMacros.filter((object) => object.name?.toLowerCase().search(filterQuery?.toLowerCase()) > -1)
    }

    return tmpMacros.map((i) => i.name)
  }

  const handleGoodXBenefitCheck = async () => {
    let planSubOptionsData = null
    let planSubOption = null

    setBenefitCheckLoading(true)

    try {
      planSubOptionsData = await getPlanSubOptions(bookingData.patientRecord.medicalAid.planOptionCode)

      if (planSubOptionsData.planSubOptions.pageResult.length > 0) {
        const tmpIndex = planSubOptionsData.planSubOptions.pageResult.findIndex(
          (option) => option.option === 'Acute Meds'
        )

        if (tmpIndex !== -1) {
          planSubOption = planSubOptionsData.planSubOptions.pageResult[tmpIndex]
        } else {
          planSubOption = planSubOptionsData.planSubOptions.pageResult[0]
        }
      }

      const ticketNo = await goodXBenefitCheck(
        bookingData,
        bookingData.patientRecord,
        bookingData.clinicRecord,
        billingItems.reduce((acc, curr) => acc + parseFloat(curr.medicalAid), 0),
        planSubOption
      )
      const response = await goodXResponse(ticketNo)
      setBenefitCheckResponse(response)
    } catch (e) {
      message.error(handleError(e, true))
    }
    setBenefitCheckLoading(false)
  }

  return (
    <div>
      <CustomRow>
        <Col span={24}>
          <Row justify='space-between'>
            <Col>
              <Space>
                <Button
                  disabled={readOnly}
                  type='primary'
                  style={{ marginBottom: 10, float: 'left' }}
                  onClick={() => setModalOpen(true)}
                >
                  <Space>
                    <FontAwesomeIcon icon={faFileLines} />
                    <span>Services Macros</span>
                  </Space>
                </Button>
                {isReception() && bookingData.patientRecord.medicalAid.name ? (
                  <Button
                    type='primary'
                    style={{ marginBottom: 10, float: 'left', backgroundColor: token.colorSuccess }}
                    onClick={() => handleGoodXBenefitCheck()}
                    loading={benefitCheckLoading}
                  >
                    <Space>
                      <FontAwesomeIcon icon={faCheck} />
                      <span>Benefit Check</span>
                    </Space>
                  </Button>
                ) : undefined}
              </Space>
            </Col>
            {authState.agiliteUser._id === bookingData.medicalProfRef ? (
              <Col>
                <Space>
                  <span>No Charge</span>
                  <Switch
                    value={bookingData.noCharge}
                    onChange={(value) => setBookingData({ ...bookingData, noCharge: value })}
                    unCheckedChildren='No'
                    checkedChildren='Yes'
                  />
                </Space>
              </Col>
            ) : null}
            {/* Invoice Type Selector */}
            <Col>
              <Space>
                <span>Invoice Type:</span>
                <Select
                  value={selectedInvoiceType}
                  onChange={(value) => {
                    setSelectedInvoiceType(value)
                    setBookingData({ ...bookingData, selectedInvoiceType: value })
                  }}
                  style={{ width: 120 }}
                  disabled={readOnly}
                >
                  <Select.Option value='private'>Private</Select.Option>
                  {bookingData.patientRecord?.medicalAid?.name && (
                    <Select.Option value='medical'>Medical Aid</Select.Option>
                  )}
                </Select>
              </Space>
            </Col>
            <Col>
              <Button
                disabled={readOnly}
                style={{ marginBottom: 10, float: 'right' }}
                onClick={() => {
                  handleGetBillingRecords()
                  setBillingSyncRequired(false)
                }}
              >
                <Space>
                  <FontAwesomeIcon icon={faRefresh} color={token.colorSuccess} />
                  <span>Refresh</span>
                </Space>
              </Button>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <BillingTool
            billing={billing}
            itemSelection={billingItems}
            setItemSelection={setBillingItems}
            readOnly={readOnly}
            bookingData={bookingData}
            isMacroForm={false}
            billingRecordsLoading={billingRecordsLoading}
            billingWebSocket={billingWebSocket}
            selectedInvoiceType={selectedInvoiceType}
          />
        </Col>
        <Modal
          title='Services Macros'
          destroyOnClose
          closeIcon={<FontAwesomeIcon icon={faXmarkCircle} color={token.colorError} />}
          closable={true}
          open={modalOpen}
          okText='Add'
          onOk={() => {
            setFilterQuery('')
            handleAddMacro()
          }}
          onCancel={() => {
            setFilterQuery('')
            setModalOpen(false)
          }}
          className='responsive-modal-macros'
        >
          <Row>
            <Col span={24} style={{ marginBottom: 12 }}>
              <Space wrap style={{ width: '100%' }}>
                <p>Search:</p>
                <Input
                  placeholder='Type to search...'
                  value={filterQuery}
                  style={{ width: 350, maxWidth: '100%' }}
                  onChange={(e) => {
                    setFilterQuery(e.target.value)
                  }}
                  addonAfter={<FontAwesomeIcon icon={faSearch} />}
                />
                <Button
                  style={{ color: token.colorError }}
                  onClick={() => {
                    setFilterQuery('')
                  }}
                >
                  Clear
                </Button>
              </Space>
            </Col>
            <Col span={24}>
              <Collapse>
                {/* Use services macros instead of billing macros */}
                {servicesMacros.map((macro, index) => {
                  // Check if service is active instead of isActive
                  if (macro.active !== false) {
                    return (
                      <Collapse.Panel
                        style={{ display: filteredMacros().includes(macro.name) ? '' : 'none' }}
                        header={macro.name}
                        key={index}
                        extra={
                          <Checkbox
                            onClick={(e) => {
                              e.stopPropagation()
                            }}
                            onChange={(e) => {
                              handleCheckboxChange(e.target.checked, macro)
                            }}
                          />
                        }
                      >
                        <Table
                          dataSource={macro.billingMacroItems}
                          size='small'
                          bordered={true}
                          pagination={false}
                          columns={[
                            { title: 'Type', dataIndex: 'type', key: 'type' },
                            { title: 'Code', dataIndex: 'code', key: 'code' },
                            { title: 'Name', dataIndex: 'name', key: 'name' },
                            { title: 'QTY', dataIndex: 'qty', key: 'qty' }
                          ]}
                        />
                      </Collapse.Panel>
                    )
                  }
                  return null
                })}
              </Collapse>
            </Col>
          </Row>
        </Modal>
      </CustomRow>
      <Modal
        title='Benefit Check Response'
        open={!!benefitCheckResponse}
        okButtonProps={{ style: { display: 'none' } }}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={() => setBenefitCheckResponse(null)}
        width={1000}
      >
        {benefitCheckResponse && (
          <div>
            <Row gutter={[12, 12]}>
              <Col span={24}>
                <Card
                  title='Medical Aid Details'
                  size='small'
                  type='inner'
                  styles={{ header: { backgroundColor: token.colorPrimary, color: token.colorWhite } }}
                >
                  <p>
                    <strong>Scheme: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.Payor[0]?.Name[0]
                    }
                  </p>
                  <p>
                    <strong>Policy Number: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.Policy[0]?.PolicyNumber[0]
                    }
                  </p>
                  <p>
                    <strong>Benefit Check Amount: </strong>R{' '}
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.Policy[0]?.BenefitCheckAmount[0]
                    }
                  </p>
                  <p>
                    <strong>Claim Option: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.Payor[0]?.ClaimOption[0]
                    }
                  </p>
                  <p>
                    <strong>Option/Plan Reference: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.Payor[0]?.OptionOrPlanReference[0]
                    }
                  </p>
                </Card>
              </Col>

              <Col span={24}>
                <Card
                  title='Practice Details'
                  size='small'
                  type='inner'
                  styles={{ header: { backgroundColor: token.colorPrimary, color: token.colorWhite } }}
                >
                  <p>
                    <strong>Practice Name: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.BillingPractice[0]?.Name[0]
                    }
                  </p>
                  <p>
                    <strong>PCNS Number: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.BillingPractice[0]?.PCNSNumber[0]
                    }
                  </p>
                  <p>
                    <strong>Speciality Code: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.BillingPractice[0]?.SpecialityCode[0]
                    }
                  </p>
                  <p>
                    <strong>Address: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.BillingPractice[0]?.ContactInformation[0]?.AddressLineOne[0]
                    }
                  </p>
                  <p>
                    <strong>Telephone: </strong>
                    {
                      benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]?.MemberValidation[0]
                        .CommonClaimHeaderInfo[0]?.BillingPractice[0]?.ContactInformation[0]?.TelephoneWork[0]
                    }
                  </p>
                </Card>
              </Col>

              <Col span={24}>
                <Card
                  title='Response Details'
                  size='small'
                  type='inner'
                  styles={{ header: { backgroundColor: token.colorSecondary, color: token.colorWhite } }}
                >
                  <Row gutter={[12, 12]}>
                    <Col span={24}>
                      <p>
                        <strong>Transaction ID: </strong>
                        {
                          benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]
                            .ResponseNumber[0]?.Transaction[0]?.$.Id
                        }
                      </p>
                      <p>
                        <strong>Claim Number: </strong>
                        {
                          benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]
                            .MemberValidation[0]?.CommonClaimHeaderInfo[0]?.ClaimNumber[0]
                        }
                      </p>
                      <p>
                        <strong>Transaction Status: </strong>
                        {
                          benefitCheckResponse.ProviderClaimResponse.CommonProviderClaimResponseInfo[0]
                            .PrimaryTrackingStatus[0]
                        }
                      </p>
                      <p>
                        <strong>Result Code: </strong>
                        {benefitCheckResponse?.ProviderClaimResponse?.MemberValidationResponse?.[0]?.ResponseDetail?.[0]
                          ?.ResultCode?.[0] === '01' ? (
                          <span style={{ color: token.colorError }}>01</span>
                        ) : (
                          <span style={{ color: token.colorSuccess }}>02</span>
                        )}
                      </p>
                      <p>
                        <strong>Result Description: </strong>
                        {benefitCheckResponse?.ProviderClaimResponse?.MemberValidationResponse?.[0]?.ResponseDetail?.[0]
                          ?.ResultCode?.[0] === '01' ? (
                          <span style={{ color: token.colorError }}>
                            {
                              benefitCheckResponse?.ProviderClaimResponse?.MemberValidationResponse?.[0]
                                ?.ResponseDetail?.[0]?.ResultDescription?.[0]
                            }
                          </span>
                        ) : (
                          <span style={{ color: token.colorSuccess }}>
                            {
                              benefitCheckResponse?.ProviderClaimResponse?.MemberValidationResponse?.[0]
                                ?.ResponseDetail?.[0]?.ResultDescription?.[0]
                            }
                          </span>
                        )}
                      </p>
                      <p>
                        <strong>Comments: </strong>
                        {
                          benefitCheckResponse?.ProviderClaimResponse?.MemberValidationResponse?.[0]
                            ?.ResponseDetail?.[0]?.Comments?.[0]
                        }
                      </p>
                    </Col>

                    <Col span={24}>
                      <Collapse defaultActiveKey={[]}>
                        {benefitCheckResponse?.ProviderClaimResponse?.MemberValidationResponse?.[0]?.ResponseDetail?.[0]?.Patient?.map(
                          (patient, index) => (
                            <Collapse.Panel
                              key={patient?.$?.id || index}
                              header={
                                <Space>
                                  <strong style={{ color: token.colorPrimary }}>{`Patient ${index + 1}:`}</strong>
                                  <span>
                                    {`${patient?.CommonPersonInfo?.[0]?.Title?.[0] || ''} ${
                                      patient?.FirstName?.[0] || ''
                                    } ${patient?.CommonPersonInfo?.[0]?.Surname?.[0] || ''}`}
                                  </span>
                                  <Tag
                                    color={
                                      patient?.MedAidStatusDesc?.[0]?.toLowerCase().includes('valid')
                                        ? 'success'
                                        : 'error'
                                    }
                                  >
                                    {patient?.MedAidStatusDesc?.[0]}
                                  </Tag>
                                </Space>
                              }
                            >
                              <div style={{ padding: '0 12px' }}>
                                <Row gutter={[24, 12]}>
                                  <Col xs={24} sm={12}>
                                    <Card size='small' title='Personal Details' bordered={false}>
                                      <p>
                                        <strong>ID Number: </strong>
                                        {patient.CommonPersonInfo[0]?.IdentityNumber[0]}
                                      </p>
                                      <p>
                                        <strong>Dependant Code: </strong>
                                        {patient.DependantCode[0]}
                                      </p>
                                      <p>
                                        <strong>Dependant Type: </strong>
                                        {patient.DependantType[0]}
                                      </p>
                                    </Card>
                                  </Col>
                                  <Col xs={24} sm={12}>
                                    <Card
                                      size='small'
                                      title='Benefit Status'
                                      bordered={false}
                                      styles={{
                                        header: {
                                          background: patient?.MedAidBenefitDesc?.[0]?.toLowerCase().includes('enough')
                                            ? token.colorSuccessBg
                                            : token.colorErrorBg
                                        }
                                      }}
                                    >
                                      <p
                                        style={{
                                          color: patient?.MedAidBenefitDesc?.[0]?.toLowerCase().includes('enough')
                                            ? token.colorSuccess
                                            : token.colorError
                                        }}
                                      >
                                        {patient?.MedAidBenefitDesc?.[0]}
                                      </p>
                                    </Card>
                                  </Col>

                                  {patient?.AuthData?.[0]?.limitavailable && (
                                    <Col span={24}>
                                      <Card size='small' title='Available Limits' bordered={false}>
                                        <Table
                                          dataSource={patient?.AuthData?.[0]?.limitavailable}
                                          size='small'
                                          pagination={false}
                                          columns={[
                                            {
                                              title: 'Description',
                                              dataIndex: ['LimitDescription', 0],
                                              key: 'description',
                                              render: (text) => text.replace('*', '')
                                            },
                                            {
                                              title: 'Available',
                                              dataIndex: ['Available', 0],
                                              key: 'available',
                                              align: 'right',
                                              render: (value) => (
                                                <span
                                                  style={{
                                                    color:
                                                      parseFloat(value) > 0 ? token.colorSuccess : token.colorError,
                                                    fontWeight: 'bold'
                                                  }}
                                                >
                                                  R {parseFloat(value).toFixed(2)}
                                                </span>
                                              )
                                            }
                                          ]}
                                        />
                                      </Card>
                                    </Col>
                                  )}
                                </Row>
                              </div>
                            </Collapse.Panel>
                          )
                        )}
                      </Collapse>
                    </Col>

                    {/* Action Buttons */}
                    <Col span={24} style={{ marginTop: 16, textAlign: 'right' }}>
                      <Space>
                        <Button onClick={() => setBenefitCheckResponse(null)}>Close</Button>
                        <Button
                          type='primary'
                          onClick={() => processBenefitsResponse(benefitCheckResponse)}
                          icon={<FontAwesomeIcon icon={faCheck} />}
                        >
                          Update Patient Accounts
                        </Button>
                      </Space>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default Billing
